import { useRouter } from "next/router";
import NextImage from "next/image";
import { useState } from "react";
import Icon from "@hackclub/icons";
import { type Timelapse, type User, type PublicUser, DraftTimelapse } from "@hackclub/lapse-api";
import { assert, validateUrl, matchOrDefault, descending } from "@hackclub/lapse-shared";

import { api } from "@/api";
import { markdownToJsx } from "@/markdown";

import { useAuth } from "@/hooks/useAuth";
import { useAsyncEffect } from "@/hooks/useAsyncEffect";
import { useAdminView } from "@/hooks/useAdminView";

import RootLayout from "@/components/layout/RootLayout";
import { ProfilePicture } from "@/components/entity/ProfilePicture";

import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { TextInput } from "@/components/ui/TextInput";
import { ErrorModal } from "@/components/layout/ErrorModal";
import { NOT_FOUND_STATUS, PageStatus, SERVER_ERROR_STATUS, StatusPage, statusForApiError } from "@/components/layout/StatusPage";
import { TimelapseGrid } from "@/components/entity/TimelapseGrid";
import { WindowedModal } from "@/components/layout/WindowedModal";
import { TextareaInput } from "@/components/ui/TextareaInput";

export default function Page() {
  const router = useRouter();
  const { currentUser } = useAuth(false);
  const adminView = useAdminView();

  const [user, setUser] = useState<User | PublicUser | null>(null);
  const [drafts, setDrafts] = useState<DraftTimelapse[] | null>(null);
  const [timelapses, setTimelapses] = useState<Timelapse[] | null>(null);

  // A profile that won't load is a dead end, and gets a page of its own - anything that goes wrong afterwards is
  // something the user can retry from where they are, and stays a modal.
  const [loadStatus, setLoadStatus] = useState<PageStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editHandle, setEditHandle] = useState("");
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editBio, setEditBio] = useState("");
  const [editUrls, setEditUrls] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const isMyself = currentUser && user && currentUser.id === user.id;

  useAsyncEffect(async () => {
    if (!router.isReady)
      return;

    const { id } = router.query;
    assert(typeof id === "string", `router.query.id was a ${typeof id} (expected a string)`);

    try {
      const userRes = await api.user.query(
        id.startsWith("@") ? { handle: id.substring(1).trim() } : { id } 
      );

      if (!userRes.ok) {
        setLoadStatus(statusForApiError(userRes.error, userRes.message));
        return;
      }

      if (!userRes.data.user) {
        setLoadStatus(NOT_FOUND_STATUS);
        return;
      }

      setUser(userRes.data.user);

      const timelapsesRes = await api.timelapse.findByUser({ user: userRes.data.user.id });
      if (!timelapsesRes.ok) {
        setLoadStatus(statusForApiError(timelapsesRes.error, timelapsesRes.message));
        return;
      }

      setTimelapses(timelapsesRes.data.timelapses);

      if (userRes.data.user.id === currentUser?.id) {
        const draftsRes = await api.draftTimelapse.findByUser({ user: userRes.data.user.id });
        if (!draftsRes.ok) {
          setError(draftsRes.message);
          return;
        }

        setDrafts(draftsRes.data.timelapses.filter(d => !d.associatedTimelapseId));
      }
    }
    catch (error) {
      console.error("([id].tsx) Error fetching user data:", error);
      setLoadStatus(SERVER_ERROR_STATUS);
    }
  }, [router.isReady, router.query]);

  const handleEditProfile = () => {
    if (!user || !isMyself)
      return;

    setEditHandle(user.handle);
    setEditDisplayName(user.displayName);
    setEditBio(user.bio);
    setEditUrls([...user.urls]);
    setEditModalOpen(true);
  };

  const handleUpdateProfile = async () => {
    if (!user || !isMyself)
      return;

    const validUrls = editUrls.filter(url => url.trim() !== "");
    const invalidUrls = validUrls.filter(url => !validateUrl(url));

    if (invalidUrls.length > 0) {
      setError(`Invalid URL${invalidUrls.length > 1 ? 's' : ''}: ${invalidUrls.join(', ')}`);
      return;
    }

    try {
      setIsUpdating(true);

      const changes: Record<string, any> = {
        displayName: editDisplayName.trim(),
        bio: editBio.trim(),
        urls: validUrls
      };

      if (editHandle.trim() !== user.handle) {
        changes.handle = editHandle.trim();
      }

      const result = await api.user.update({
        id: user.id,
        changes
      });

      if (result.ok) {
        setUser(result.data.user);
        setEditModalOpen(false);
      }
      else {
        setError(`Failed to update profile: ${result.message}`);
      }
    }
    catch (error) {
      console.error("([id].tsx) error updating profile:", error);
      setError("Failed to update profile");
    }
    finally {
      setIsUpdating(false);
    }
  };

  function formatJoinDate(timestamp: number) {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long"
    });
  };

  if (loadStatus) {
    return <StatusPage {...loadStatus} />;
  }

  return (
    <RootLayout showHeader={true} title={user ? `${user.displayName} (@${user.handle}) - Lapse` : "Lapse"}>
      <div className="px-8 py-8 md:py-0 sm:px-16">
        <div className="flex flex-col gap-6 md:gap-0 md:flex-row justify-between items-center md:px-16">
          <div className="flex flex-col md:flex-row gap-4 md:gap-8 w-full md:w-auto">
            <ProfilePicture
              user={user}
              size="lg"
              className="w-24 h-24 text-2xl"
            />

            <div className="flex flex-col">
              <h1 className="text-4xl font-bold wrap-break-word">{ user ? user.displayName : <Skeleton className="w-48" /> }</h1>
              <p className="text-secondary text-lg m-0">{ user ? `@${user.handle}` : <Skeleton className="w-32 h-3!" /> }</p>

              { user?.bio && user.bio.trim().length > 0 && (
                <p className="text-smoke text-lg leading-relaxed mb-4 max-w-2xl mt-2">
                  { markdownToJsx(user.bio) }
                </p>
              ) }

              <div className="flex flex-col text-muted">
                <div className="flex items-center gap-2">
                  <Icon glyph="clock" size={16} />
                  <span>{ user ? `Joined ${formatJoinDate(user.createdAt)}` : <Skeleton className="w-64 h-3!" /> }</span>
                </div>

                { (user?.urls ?? []).length > 0 && (
                  user!.urls.map(url => (
                    <div key={url} className="flex items-center gap-2">
                      <Icon
                         size={16}
                         glyph={
                           matchOrDefault(new URL(url).hostname, {
                             "x.com": "twitter", // seriously?
                             "twitter.com": "twitter",
                             "twitch.tv": "twitch",
                             "github.com": "github",
                             "messenger.com": "messenger-fill",
                             "instagram.com": "instagram",
                             "hackclub.slack.com": "slack-fill",
                             "medium.com": "medium-fill",
                             "facebook.com": "facebook",
                             "youtube.com": "youtube",
                             "youtu.be": "youtube"
                           }) ?? "link"
                         }
                       />

                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan hover:underline"
                      >
                        {new URL(url).hostname}{new URL(url).pathname}
                      </a>
                  </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col min-[950px]:flex-row gap-2 md:gap-4 w-full md:w-auto">
            { user && adminView.isAdmin && (
              <Button icon="admin" href={`/admin/user/${user.id}`}>
                Admin profile
              </Button>
            ) }

            { user && user.slackId && (
              <Button icon="slack-fill" onClick={() => window.open(`https://hackclub.slack.com/team/${user.slackId}`, "_blank")}>
                Open in Slack
              </Button>
            ) }

            { isMyself && (
              <Button icon="edit" onClick={handleEditProfile}>
                Edit Profile
              </Button>
            ) }
          </div>
        </div>

        {
          (drafts && drafts.length > 0) && (
            <div className="pb-8 pt-12 px-0 sm:px-16">
              <div className="border-dashed border-black border rounded-xl p-12 flex flex-col gap-8">
                <div className="flex gap-4">
                  <NextImage
                    src="/images/orpheus-time.png" alt=""
                    width={1200} height={1200}
                    className="w-16 h-16"
                  />

                  <div className="flex flex-col gap-1">
                    <h1 className="font-bold text-3xl">Draft timelapses</h1>
                    <p className="text-lg">You have some timelapses that haven't been published yet!</p>
                  </div>
                </div>

                <TimelapseGrid timelapses={drafts.toSorted(descending(x => x.createdAt))} className="gap-y-16" />
              </div>
            </div>
          )
        }

        {
          // `timelapses` is null until the request lands, so an empty profile doesn't flash this while loading.
          (timelapses && timelapses.length === 0) ? (
            // Sized to what's left of the viewport under the profile header, so the notice sits in the middle of
            // the empty space rather than right below the bio.
            <div className="flex flex-col items-center justify-center gap-4 py-16 px-0 sm:px-16 min-h-[50vh] sm:min-h-[calc(100vh-24rem)]">
              {!isMyself && (
                <NextImage
                  src="/images/waaaah_dino.png" alt=""
                  width={128} height={106}
                  className="w-24 h-auto"
                />
              )}

              <p className="text-muted text-center wrap-break-word">
                {
                  isMyself
                    ? <>Looks like you don&apos;t have any timelapses. Go make one!</>
                    : <>Looks like this user hasn&apos;t made any timelapses yet.</>
                }
              </p>

              {/* Only the owner of the profile has anything to do about it. */}
              {isMyself && (
                <Button href="/timelapse/create" kind="primary" icon="plus-fill">
                  Create
                </Button>
              )}
            </div>
          ) : (
            <TimelapseGrid timelapses={timelapses ?? []} className="gap-y-16 py-16 px-0 sm:px-16" />
          )
        }
      </div>

      <WindowedModal
        icon="edit"
        title="Edit Profile"
        description="Update your profile information."
        isOpen={editModalOpen}
        setIsOpen={setEditModalOpen}
      >
        <div className="flex flex-col gap-6">
          <TextInput
            field={{
              label: "Handle",
              description: "Your unique username. You can change this once per day."
            }}
            value={editHandle}
            onChange={setEditHandle}
            maxLength={16}
          />

          <TextInput
            field={{
              label: "Display Name",
              description: "Your public display name."
            }}
            value={editDisplayName}
            onChange={setEditDisplayName}
            maxLength={24}
          />

          <TextareaInput
            label="Bio"
            description="Tell others about yourself. Optional."
            value={editBio}
            onChange={setEditBio}
            maxLength={160}
          />

          <div className="flex flex-col gap-4">
            <div>
              <label className="text-smoke font-semibold">Featured URLs</label>
              <p className="text-muted">
                Add links to your website, social media, or other profiles. Maximum of 4 URLs.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              {editUrls.map((url, index) => (
                <div key={index} className="flex gap-2">
                  <TextInput
                    value={url}
                    placeholder="https://example.com"
                    maxLength={64}
                    onChange={(x) => {
                      const newUrls = [...editUrls];
                      newUrls[index] = x;
                      setEditUrls(newUrls);
                    }}
                  />
                  
                  <Button
                    kind="regular"
                    onClick={() => {
                      const newUrls = editUrls.filter((_, i) => i !== index);
                      setEditUrls(newUrls);
                    }}
                    className="px-3"
                  >
                    <Icon glyph="delete" size={24} />
                  </Button>
                </div>
              ))}

              {editUrls.length < 4 && (
                <Button
                  kind="regular"
                  onClick={() => setEditUrls([...editUrls, ""])}
                  className="gap-2 w-full"
                >
                  <Icon glyph="plus" size={16} />
                  Add URL
                </Button>
              )}
            </div>
          </div>

          <Button
            onClick={handleUpdateProfile}
            disabled={isUpdating || !editDisplayName.trim()}
            kind="primary"
          >
            {isUpdating ? "Updating..." : "Update Profile"}
          </Button>
        </div>
      </WindowedModal>

      <ErrorModal
        isOpen={!!error}
        setIsOpen={(open) => !open && setError(null)}
        message={error || ""}
      />
    </RootLayout>
  );
}
